/**
 * Settings › Voice › Listening › Engine: one menu with the platform
 * recogniser and each local dictation model, one marked Recommended for the
 * languages the user speaks (`use-dictation-engine-menu.ts`). The line under
 * it says what the choice is for, or how its download goes; below it, a
 * failed download offers a retry and a downloaded model nobody uses can be
 * removed to free its space.
 */
import { useState } from "react";

import { Button, ConfirmDialog, PocketRow, SelectField } from "@/components/ui";
import type { DictationEngineChoice } from "@/desktop/dictation-engine-options";
import {
  useDictationEngineMenu,
  type SpareDictationModel,
} from "@/desktop/use-dictation-engine-menu";
import { useT } from "@/i18n";
import { settingsVoiceMessages } from "@/i18n/messages/settings-voice";
import { DictationEngineMenuError } from "./dictation-engine-menu-error";

export function VoiceDictationEngineRow({
  onChoose,
}: {
  onChoose: (engine: DictationEngineChoice) => void;
}) {
  const t = useT(settingsVoiceMessages);
  const menu = useDictationEngineMenu({ onChoose });
  const [removing, setRemoving] = useState<SpareDictationModel | null>(null);
  const [busy, setBusy] = useState(false);
  if (!menu.isSelectable) return null;

  const line = menu.download
    ? t("modelDownloadingLine", menu.download)
    : menu.hint;
  const hasBelow =
    Boolean(menu.failed || menu.error) || menu.spare.length > 0 || removing;

  const confirmRemove = async () => {
    if (!removing) return;
    setBusy(true);
    await menu.remove(removing.id);
    setBusy(false);
    setRemoving(null);
  };

  return (
    <PocketRow
      testId="settings-row-voice-dictation-engine"
      title={t("engine")}
      secondLine={line}
      trailing={
        <SelectField
          variant="value"
          value={menu.value}
          options={menu.options}
          onChange={menu.choose}
          testId="voice-dictation-engine"
          ariaLabel={t("dictationEngine")}
        />
      }
      below={
        hasBelow ? (
          <>
            <DictationEngineMenuError
              menu={menu}
              testId="voice-dictation-engine"
            />
            {menu.spare.map((model) => (
              <p
                key={model.id}
                className="flex basis-full items-center gap-2 text-appkit-mini text-muted"
                data-testid={`voice-dictation-model-spare-${model.id}`}
              >
                {t("modelSpareLine", {
                  name: model.name,
                  size: model.megabytes,
                })}
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setRemoving(model)}
                  data-testid={`voice-dictation-model-remove-${model.id}`}
                >
                  {t("modelRemove")}
                </Button>
              </p>
            ))}
            {removing ? (
              <ConfirmDialog
                busy={busy}
                title={t("modelRemoveTitle", { name: removing.name })}
                description={t("modelRemoveBody", { size: removing.megabytes })}
                confirmLabel={t("modelRemove")}
                onConfirm={() => void confirmRemove()}
                onCancel={() => {
                  if (!busy) setRemoving(null);
                }}
                data-testid="voice-dictation-model-remove-dialog"
              />
            ) : null}
          </>
        ) : null
      }
    />
  );
}
